/*
 *
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Aegis AO Soft LLC and Alexander Orlov.
 *
 * This code may be used, reproduced, modified, or distributed ONLY with the
 * prior written permission of Aegis AO Soft LLC / Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft LLC
 *
 */


import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from './Button';
import type { BookingsDTO } from '@/types';

/**
 * Carried in history state when the Booking Info modal sends the user off to the Drivers or
 * Vehicles page. The booking ROW travels with it — not just its id — so the modal can reopen
 * without the bookings list having to be back on the same page/filter it was on.
 */
export interface BookingReturnState {
  /** Bookings URL (path + query) the modal was opened from. */
  url: string;
  /** The booking whose Booking Info modal to reopen. */
  booking: BookingsDTO;
}

export function getBookingReturn(state: unknown): BookingReturnState | null {
  const ret = (state as { returnToBooking?: BookingReturnState } | null)?.returnToBooking;
  return ret && ret.url && ret.booking ? ret : null;
}

/**
 * "Back to booking" — rendered only when the page was reached FROM the Booking Info modal.
 * Returns to that bookings view and hands the booking back so the modal reopens where it was.
 */
export const BackToBookingButton: React.FC<{ className?: string }> = ({ className }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const ret = getBookingReturn(location.state);
  if (!ret) return null;

  return (
    <Button
      variant="outline"
      className={className}
      onClick={() => navigate(ret.url, { state: { openInfoBooking: ret.booking } })}
    >
      <ArrowLeft className="h-4 w-4" />
      <span className="hidden md:inline ml-1">{t('bookingsPage.backToBooking', 'Back to Booking')}</span>
    </Button>
  );
};
